import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Paper,
  TextField
} from '@mui/material'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'

import { changePassword, changeUsername, logoutUser } from 'store/actions/user'
import { selectUser } from 'store/selectors/user'

const UserChangeLogin = () => {
  const dispatch = useDispatch()
  const { i18n } = useTranslation()
  const t = i18n.getResourceBundle(i18n.language)

  const { username } = useSelector(selectUser)

  const [usernameState, setUsernameState] = useState({
    showDialog: false,
    username: '',
    password: '',
    isValid: false,
    errors: {
      username: '',
      password: '',
      other: ''
    }
  })

  const [passwordState, setPasswordState] = useState({
    showDialog: false,
    password: '',
    newPassword: '',
    repeatPassword: '',
    isValid: false,
    errors: {
      password: '',
      newPassword: '',
      repeatPassword: '',
      other: ''
    }
  })

  const handleNewUsernameChange = e => {
    validateUsernameState(e.target.value, usernameState.password)
    setUsernameState(state => ({ ...state, username: e.target.value }))
  }

  const handleNewUsernamePasswordChange = e => {
    validateUsernameState(usernameState.username, e.target.value)
    setUsernameState(state => ({ ...state, password: e.target.value }))
  }

  const handleCurrentPasswordChange = e => {
    validatePasswordState(e.target.value, passwordState.newPassword, passwordState.repeatPassword)
    setPasswordState(state => ({ ...state, password: e.target.value }))
  }

  const handleNewPasswordChange = e => {
    validatePasswordState(passwordState.password, e.target.value, passwordState.repeatPassword)
    setPasswordState(state => ({ ...state, newPassword: e.target.value }))
  }

  const handleRepeatPasswordChange = e => {
    validatePasswordState(passwordState.password, passwordState.newPassword, e.target.value)
    setPasswordState(state => ({ ...state, repeatPassword: e.target.value }))
  }

  const validateUsernameState = (newUsername, currentPassword) => {
    const empty = ''
    const errors = []

    if (newUsername === empty) {
      errors.push({ username: t.user.fieldRequired })
    } else if (newUsername === username) {
      errors.push({ username: t.user.profile.newUsernameError })
    }
    if (currentPassword === empty) {
      errors.push({ password: t.user.fieldRequired })
    }

    setUsernameState(state => ({
      ...state,
      errors: Object.assign({}, ...errors),
      isValid: errors.length === 0
    }))
  }

  const validatePasswordState = (current, newPassword, repeat) => {
    const empty = ''
    const errors = []

    if (current === empty) {
      errors.push({ password: t.user.fieldRequired })
    }
    if (newPassword === empty) {
      errors.push({ newPassword: t.user.fieldRequired })
    } else if (newPassword === current) {
      errors.push({ newPassword: t.user.profile.newPasswordError })
    } else if (newPassword !== repeat) {
      errors.push({ newPassword: t.user.updatePasswordError })
    }
    if (repeat === empty) {
      errors.push({ repeatPassword: t.user.fieldRequired })
    }

    setPasswordState(state => ({
      ...state,
      errors: Object.assign({}, ...errors),
      isValid: errors.length === 0
    }))
  }

  const submitNewUsername = async () => {
    const result = await dispatch(changeUsername(usernameState.password, usernameState.username))
    if (result.success) {
      dispatch(logoutUser())
    } else {
      setUsernameState(state => ({ ...state, errors: result.errors }))
    }
  }

  const submitNewPassword = async () => {
    const result = await dispatch(changePassword(passwordState.password, passwordState.newPassword))
    if (result.success) {
      dispatch(logoutUser())
    } else {
      setPasswordState(state => ({ ...state, errors: result.errors }))
    }
  }

  return (
    <Grid className="user-change-login">
      <h3>{t.user.profile.changeLogin}</h3>
      <p>{t.user.profile.changeLoginDescription}</p>
      <Grid container spacing={4}>
        <Grid item xs={12} md={6}>
          <Paper>
            <h4>{t.user.profile.usernameTitle}</h4>
            <p>{username}</p>
            <Divider />
            <Button
              variant="text"
              onClick={() => setUsernameState({ ...usernameState, showDialog: true })}
            >
              {t.user.profile.changeUsernameButton}
            </Button>
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper>
            <h4>{t.user.profile.passwordTitle}</h4>
            <p>&bull;&bull;&bull;&bull;&bull;&bull;&bull;&bull;</p>
            <Divider />
            <Button
              variant="text"
              onClick={() => setPasswordState({ ...passwordState, showDialog: true })}
            >
              {t.user.profile.changePasswordButton}
            </Button>
          </Paper>
        </Grid>
      </Grid>
      <Dialog
        className="user-change-login-dialog"
        open={usernameState.showDialog}
        onClose={() => setUsernameState({ ...usernameState, showDialog: false })}
      >
        <DialogTitle>{t.user.profile.changeUsernameDialogTitle}</DialogTitle>
        <DialogContent>
          <Grid container direction="column" spacing={2}>
            <TextField
              id="username"
              label={t.user.profile.actualUsername}
              value={username || ''}
              variant="standard"
              autoComplete="off"
            />
            <TextField
              id="newusername"
              label={t.user.profile.newUsername}
              onChange={handleNewUsernameChange}
              error={!!usernameState.errors.username}
              helperText={usernameState.errors.username}
              variant="standard"
            />
            <TextField
              id="currentpassword"
              type="password"
              label={t.user.profile.actualPassword}
              onChange={handleNewUsernamePasswordChange}
              error={!!usernameState.errors.password}
              helperText={usernameState.errors.password}
              variant="standard"
            />
            {usernameState.errors.other && (
              <p className="message-danger">{usernameState.errors.other}</p>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUsernameState({ ...usernameState, showDialog: false })}>
            {t.global.cancel}
          </Button>
          <Button variant="contained" disabled={!usernameState.isValid} onClick={submitNewUsername}>
            {t.global.submit}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        className="user-change-login-dialog"
        open={passwordState.showDialog}
        onClose={() => setPasswordState({ ...passwordState, showDialog: false })}
      >
        <DialogTitle>{t.user.profile.changePasswordDialogTitle}</DialogTitle>
        <DialogContent>
          <Grid container direction="column" spacing={2}>
            <TextField
              id="actualpassword"
              type="password"
              label={t.user.profile.actualPassword}
              onChange={handleCurrentPasswordChange}
              error={!!passwordState.errors.password}
              helperText={passwordState.errors.password}
              variant="standard"
            />
            <TextField
              id="newpassword"
              type="password"
              label={t.user.updatePassword}
              onChange={handleNewPasswordChange}
              error={!!passwordState.errors.newPassword}
              helperText={passwordState.errors.newPassword}
              variant="standard"
            />
            <TextField
              id="newpasswordrepeat"
              type="password"
              label={t.user.updatePasswordRepeat}
              onChange={handleRepeatPasswordChange}
              error={!!passwordState.errors.repeatPassword}
              helperText={passwordState.errors.repeatPassword}
              variant="standard"
            />
            {passwordState.errors.other && (
              <p className="message-danger">{passwordState.errors.other}</p>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPasswordState({ ...passwordState, showDialog: false })}>
            {t.global.cancel}
          </Button>
          <Button variant="contained" disabled={!passwordState.isValid} onClick={submitNewPassword}>
            {t.global.submit}
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  )
}

UserChangeLogin.tabPath = 'change-login'
UserChangeLogin.getTabName = t => t.user.profile.changeLogin

export default UserChangeLogin
